import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ShieldAlert } from 'lucide-react'
import { api, ApiError } from '@/lib/api/client'
import { useAuth } from '@/lib/auth'
import { formatDate } from '@/lib/utils'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { TableSkeleton } from '@/components/common/skeleton'
import { ErrorState } from '@/components/common/error-state'
import { EmptyState } from '@/components/common/empty-state'

type UserRole = 'admin' | 'viewer'

function primaryRole(roles: string[]): UserRole {
  return roles.includes('admin') ? 'admin' : 'viewer'
}

function roleBadgeVariant(role: UserRole): 'default' | 'secondary' {
  return role === 'admin' ? 'default' : 'secondary'
}

function apiErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    if (err.status === 501) {
      return 'User management is not configured on this deployment.'
    }
    if (err.status === 403) {
      return 'You do not have permission to perform this action.'
    }
    return err.message || fallback
  }
  return err instanceof Error ? err.message : fallback
}

export function UsersPage() {
  const { isAdmin } = useAuth()
  const queryClient = useQueryClient()

  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState<UserRole>('viewer')
  const [inviteSuccess, setInviteSuccess] = useState<string | null>(null)

  const usersQuery = useQuery({
    queryKey: ['users'],
    queryFn: () => api.listUsers(),
    enabled: isAdmin,
  })

  const inviteMutation = useMutation({
    mutationFn: () => api.inviteUser(inviteEmail.trim(), inviteRole),
    onSuccess: (data) => {
      setInviteSuccess(`Invitation sent to ${data.email}`)
      setInviteEmail('')
      queryClient.invalidateQueries({ queryKey: ['users'] })
    },
    onError: () => {
      setInviteSuccess(null)
    },
  })

  const roleMutation = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: UserRole }) =>
      api.setUserRole(userId, role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
    },
  })

  if (!isAdmin) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight">Users</h2>
          <p className="text-sm text-muted-foreground">Manage dashboard access</p>
        </div>
        <Card className="border-destructive/30">
          <CardContent className="flex items-start gap-3 py-8">
            <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
            <div>
              <h3 className="font-semibold">Forbidden</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                User management is available to admin users only. You are signed in as a viewer.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  const handleInviteSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setInviteSuccess(null)
    if (!inviteEmail.trim()) return
    inviteMutation.mutate()
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold tracking-tight">Users</h2>
        <p className="text-sm text-muted-foreground">
          Invite team members and manage dashboard roles
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Invite user</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleInviteSubmit} className="flex flex-col gap-4 sm:flex-row sm:items-end">
            <div className="flex-1 space-y-2">
              <label htmlFor="invite-email" className="text-sm font-medium">
                Email
              </label>
              <Input
                id="invite-email"
                type="email"
                placeholder="colleague@example.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                required
                disabled={inviteMutation.isPending}
              />
            </div>
            <div className="w-full space-y-2 sm:w-40">
              <label htmlFor="invite-role" className="text-sm font-medium">
                Role
              </label>
              <Select
                id="invite-role"
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value as UserRole)}
                disabled={inviteMutation.isPending}
              >
                <option value="viewer">Viewer</option>
                <option value="admin">Admin</option>
              </Select>
            </div>
            <Button type="submit" disabled={inviteMutation.isPending || !inviteEmail.trim()}>
              {inviteMutation.isPending ? 'Sending…' : 'Send invite'}
            </Button>
          </form>
          {inviteSuccess && (
            <p className="mt-4 text-sm text-emerald-700">{inviteSuccess}</p>
          )}
          {inviteMutation.isError && (
            <p className="mt-4 text-sm text-destructive">
              {apiErrorMessage(inviteMutation.error, 'Failed to send invitation')}
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Team members</CardTitle>
        </CardHeader>
        <CardContent>
          {usersQuery.isLoading ? (
            <TableSkeleton rows={5} />
          ) : usersQuery.isError ? (
            <ErrorState
              title={
                usersQuery.error instanceof ApiError && usersQuery.error.status === 501
                  ? 'Not configured'
                  : undefined
              }
              message={apiErrorMessage(usersQuery.error, 'Failed to load users')}
              onRetry={() => usersQuery.refetch()}
            />
          ) : !usersQuery.data || usersQuery.data.users.length === 0 ? (
            <EmptyState message="No users found. Send an invitation to add team members." />
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Last active</TableHead>
                    <TableHead className="w-[140px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {usersQuery.data.users.map((user) => {
                    const role = primaryRole(user.roles)
                    const isUpdating =
                      roleMutation.isPending && roleMutation.variables?.userId === user.id

                    return (
                      <TableRow key={user.id}>
                        <TableCell className="font-medium">{user.email}</TableCell>
                        <TableCell>
                          <Badge variant={roleBadgeVariant(role)}>
                            {role === 'admin' ? 'Admin' : 'Viewer'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {formatDate(user.created_at)}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {user.last_active_at ? formatDate(user.last_active_at) : '—'}
                        </TableCell>
                        <TableCell>
                          <Select
                            value={role}
                            disabled={isUpdating}
                            onChange={(e) => {
                              const nextRole = e.target.value as UserRole
                              if (nextRole !== role) {
                                roleMutation.mutate({ userId: user.id, role: nextRole })
                              }
                            }}
                            className="h-9"
                          >
                            <option value="viewer">Viewer</option>
                            <option value="admin">Admin</option>
                          </Select>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
              {roleMutation.isError && (
                <p className="mt-4 text-sm text-destructive">
                  {apiErrorMessage(roleMutation.error, 'Failed to update role')}
                </p>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
